import React from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { CheckCircle2, TrendingUp, Users, Search, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { useI18n } from "@/contexts/I18nContext";

export default function ForBusinessesPage() {
  const { t } = useI18n();

  return (
    <div className="flex-1 w-full">
      {/* Hero */}
      <section className="bg-secondary text-secondary-foreground py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 z-0 bg-[url('https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=2940&auto=format&fit=crop')] bg-cover bg-center opacity-10"></div>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">{t("Reach Customers Who Prefer USDT")}</h1>
          <p className="text-xl md:text-2xl text-secondary-foreground/80 mb-10 font-light">
            {t("List your Dubai business on Souq Crypto and connect with customers searching for products and services from USDT-friendly merchants.")}
          </p>
          <div className="flex justify-center gap-4">
            <Button size="lg" className="h-14 px-8 text-lg font-bold" asChild data-testid="btn-apply-to-list">
              <Link href="/list-your-business">{t("Apply to List Your Business")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-24 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">{t("Why list on Souq Crypto?")}</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t("Connect with customers searching for USDT-friendly businesses in Dubai.")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-card p-6 rounded-2xl border border-border shadow-sm">
              <TrendingUp className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">{t("Increased Visibility")}</h3>
              <p className="text-muted-foreground">{t("Stand out in a focused directory for USDT-friendly businesses in Dubai.")}</p>
            </div>
            <div className="bg-card p-6 rounded-2xl border border-border shadow-sm">
              <Users className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">{t("Direct Customer Relationships")}</h3>
              <p className="text-muted-foreground">{t("Buyers contact you directly via WhatsApp, phone, or email.")}</p>
            </div>
            <div className="bg-card p-6 rounded-2xl border border-border shadow-sm">
              <ShieldAlert className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">{t("No Transaction Fees")}</h3>
              <p className="text-muted-foreground">{t("We do not process payments or take transaction fees. All funds go directly between buyer and merchant.")}</p>
            </div>
            <div className="bg-card p-6 rounded-2xl border border-border shadow-sm">
              <Search className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">{t("Category & Keyword Search")}</h3>
              <p className="text-muted-foreground">{t("Buyers filter by category, area, product type, and keywords to find exactly what they need — surfacing your listings to the right people.")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-4 bg-muted/30 border-y border-border">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">{t("Listing Options")}</h2>
            <p className="text-muted-foreground text-lg">{t("Choose the option that fits your business.")}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="flex flex-col border-border shadow-md">
              <CardHeader className="p-8 pb-4 text-center">
                <CardTitle className="text-2xl mb-2">{t("Basic Listing")}</CardTitle>
                <div className="text-4xl font-extrabold mb-2">{t("Free")}</div>
                <p className="text-muted-foreground text-sm">{t("Free basic listings are available during the launch period.")}</p>
              </CardHeader>
              <CardContent className="p-8 pt-4 flex-1">
                <ul className="space-y-4">
                  <li className="flex gap-3 items-start"><CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> <span>{t("Standard merchant profile")}</span></li>
                  <li className="flex gap-3 items-start"><CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> <span>{t("Up to 5 product/service listings")}</span></li>
                  <li className="flex gap-3 items-start"><CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> <span>{t("Direct WhatsApp & Email leads")}</span></li>
                  <li className="flex gap-3 items-start"><CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> <span>{t("Standard search visibility")}</span></li>
                </ul>
              </CardContent>
              <CardFooter className="p-8 pt-0">
                <Button className="w-full h-12 text-base" variant="outline" asChild data-testid="btn-get-listed-free">
                  <Link href="/list-your-business?plan=basic">{t("Get Listed Free")}</Link>
                </Button>
              </CardFooter>
            </Card>

            <Card className="flex flex-col border-primary shadow-xl shadow-primary/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 text-xs font-bold rounded-bl-lg uppercase tracking-wider">{t("Recommended")}</div>
              <CardHeader className="p-8 pb-4 text-center">
                <CardTitle className="text-2xl mb-2 text-primary">{t("Featured Partner")}</CardTitle>
                <div className="text-4xl font-extrabold mb-2" dir="ltr">{t("AED 499/mo")}</div>
                <p className="text-muted-foreground">{t("Maximum visibility for established businesses.")}</p>
              </CardHeader>
              <CardContent className="p-8 pt-4 flex-1">
                <ul className="space-y-4">
                  <li className="flex gap-3 items-start"><CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> <span>{t("Everything in Basic")}</span></li>
                  <li className="flex gap-3 items-start"><CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> <span className="font-semibold">{t("Unlimited listings")}</span></li>
                  <li className="flex gap-3 items-start"><CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> <span className="font-semibold">{t("Homepage placement")}</span></li>
                  <li className="flex gap-3 items-start"><CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> <span className="font-semibold">{t("Featured badge on all listings")}</span></li>
                  <li className="flex gap-3 items-start"><CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> <span className="font-semibold">{t("Priority in search results")}</span></li>
                </ul>
              </CardContent>
              <CardFooter className="p-8 pt-0 flex flex-col">
                <Button className="w-full h-12 text-base font-bold shadow-lg hover:shadow-primary/20" asChild data-testid="btn-apply-early-access">
                  <Link href="/list-your-business?plan=featured">{t("Apply for Early Access")}</Link>
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-4 w-full">{t("No payment required today. We will contact you.")}</p>
              </CardFooter>
            </Card>
          </div>
        </div>
      </section>

      {/* How it works for merchants */}
      <section className="py-24 px-4 bg-background">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-12">{t("How listing works")}</h2>

          <div className="grid md:grid-cols-3 gap-8 text-left relative">
            <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-0.5 bg-border -translate-y-1/2 z-0"></div>

            <div className="relative z-10 bg-card p-6 rounded-xl border border-border shadow-sm">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center mb-4">1</div>
              <h3 className="font-bold text-lg mb-2">{t("Apply")}</h3>
              <p className="text-muted-foreground text-sm">{t("Fill out the application form with your business details.")}</p>
            </div>

            <div className="relative z-10 bg-card p-6 rounded-xl border border-border shadow-sm">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center mb-4">2</div>
              <h3 className="font-bold text-lg mb-2">{t("Review")}</h3>
              <p className="text-muted-foreground text-sm">{t("Our team reviews your application and may be in touch with questions.")}</p>
            </div>

            <div className="relative z-10 bg-card p-6 rounded-xl border border-border shadow-sm">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center mb-4">3</div>
              <h3 className="font-bold text-lg mb-2">{t("Go Live")}</h3>
              <p className="text-muted-foreground text-sm">{t("Your listings go live. Buyers contact you directly to arrange USDT payment.")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 bg-muted/30 border-t border-border/40">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-start gap-3 text-sm text-muted-foreground">
            <ShieldAlert className="h-5 w-5 shrink-0 text-accent mt-0.5" />
            <p>{t("Souq Crypto is an independent business directory. We do not exchange, receive, hold, transfer, or process cryptocurrency or customer funds. Customers and merchants arrange transactions independently and are responsible for confirming all payment terms.")}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
